import { useEffect, useRef, type ReactNode } from 'react';
import { Symbol } from './symbol';

/**
 * The create/edit surface shared by every master-data screen.
 *
 * The console already had six hand-rolled `fixed inset-0` drawers, each with a
 * slightly different backdrop opacity and none of them closing on Escape or
 * restoring focus. This is one drawer that does those things, so the master
 * pages don't add a seventh variant.
 */
interface DrawerProps {
  open: boolean;
  title: string;
  subtitle?: string;
  onClose: () => void;
  onSubmit: () => void;
  submitLabel?: string;
  busy?: boolean;
  /** Rendered bottom-left, opposite the save button — usually Delete. */
  secondaryAction?: ReactNode;
  children: ReactNode;
}

export function CrudDrawer({
  open,
  title,
  subtitle,
  onClose,
  onSubmit,
  submitLabel = 'Save',
  busy = false,
  secondaryAction,
  children,
}: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Latest onClose/busy for the Escape handler. Pages usually pass an inline
  // onClose, so depending on it would re-run the effect on every keystroke —
  // and the effect's focus() would yank the cursor back to the first field.
  const latest = useRef({ onClose, busy });
  latest.current = { onClose, busy };

  // Runs once per open, never while the user is typing.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !latest.current.busy) latest.current.onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Focus the first control so a keyboard user lands inside the form rather
    // than at the top of the document behind the backdrop.
    panelRef.current?.querySelector<HTMLElement>('input, select, textarea')?.focus();
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close"
        onClick={() => !busy && onClose()}
        className="absolute inset-0 h-full w-full cursor-default bg-scrim motion-safe:animate-fadeIn"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="relative flex h-full w-[min(560px,100vw)] flex-col bg-surface-card shadow-elev-4 motion-safe:animate-slideInRight"
      >
        <header className="flex shrink-0 items-start gap-3 border-b border-surface-line px-6 pb-4 pt-5">
          <div className="min-w-0 flex-1">
            <h2 className="font-display text-drawer-title text-ink-900">{title}</h2>
            {subtitle && <p className="mt-1 text-[14px] text-ink-500">{subtitle}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            className="-mr-2 grid h-[34px] w-[34px] shrink-0 place-items-center rounded-full text-ink-500 transition-colors hover:bg-surface-2 hover:text-ink-900 disabled:opacity-50"
          >
            <Symbol name="close" size={20} />
          </button>
        </header>

        <form
          className="flex min-h-0 flex-1 flex-col"
          onSubmit={(e) => {
            e.preventDefault();
            onSubmit();
          }}
        >
          <div className="flex-1 space-y-5 overflow-y-auto px-6 py-5">{children}</div>

          <footer className="flex shrink-0 items-center gap-2.5 border-t border-surface-line bg-surface-card px-6 py-3.5">
            <div className="flex-1">{secondaryAction}</div>
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="btn btn-primary"
            >
              {busy ? 'Saving…' : submitLabel}
            </button>
          </footer>
        </form>
      </div>
    </div>
  );
}

// ---------- Field primitives -------------------------------------------------

interface FieldShellProps {
  label: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

export function Field({ label, hint, required, children }: FieldShellProps) {
  return (
    <label className="block">
      <span className="field-label mb-1.5">
        {label}
        {required && <span className="ml-0.5 text-danger-ink">*</span>}
      </span>
      {children}
      {hint && <span className="field-help mt-1.5">{hint}</span>}
    </label>
  );
}

// v2 input: 42px, radius 8, ink-300 border, accent border + 3px ring on focus.
const inputClass = 'input';

interface TextFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  maxLength?: number;
  mono?: boolean;
}

export function TextField({ label, value, onChange, placeholder, hint, required, disabled, maxLength, mono }: TextFieldProps) {
  return (
    <Field label={label} hint={hint} required={required}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={maxLength}
        className={`${inputClass} ${mono ? 'font-mono' : ''}`}
      />
    </Field>
  );
}

interface TextAreaFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  rows?: number;
}

export function TextAreaField({ label, value, onChange, placeholder, hint, rows = 3 }: TextAreaFieldProps) {
  return (
    <Field label={label} hint={hint}>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className={inputClass}
      />
    </Field>
  );
}

interface NumberFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  hint?: string;
  required?: boolean;
  step?: string;
  min?: number;
  max?: number;
}

export function NumberField({ label, value, onChange, placeholder, hint, required, step, min, max }: NumberFieldProps) {
  return (
    <Field label={label} hint={hint} required={required}>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        step={step}
        min={min}
        max={max}
        className={`${inputClass} font-mono`}
      />
    </Field>
  );
}

interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export function SelectField({ label, value, onChange, options, hint, required, disabled, placeholder }: SelectFieldProps) {
  return (
    <Field label={label} hint={hint} required={required}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className={inputClass}
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </Field>
  );
}

interface CheckFieldProps {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}

export function CheckField({ label, hint, checked, onChange, disabled }: CheckFieldProps) {
  return (
    <label className="flex cursor-pointer items-start gap-2.5">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="check mt-0.5 shrink-0 disabled:opacity-50"
      />
      <span className="min-w-0">
        <span className="field-label">{label}</span>
        {hint && <span className="field-help mt-0.5">{hint}</span>}
      </span>
    </label>
  );
}

/** Danger-toned button for a drawer's `secondaryAction` slot. */
export function DeleteButton({ onClick, disabled, label = 'Delete' }: { onClick: () => void; disabled?: boolean; label?: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="btn btn-danger-outline"
    >
      {label}
    </button>
  );
}

/** Re-exported so the master-data tabs keep one import. */
export { extractErr } from '../api/error-message';
